/**
 * CR-01 §3: one serialized connection controller per account and transport
 * owner.
 *
 * Everything that can change connection state — socket callbacks, timers,
 * authentication results, network notifications, local database completions —
 * posts an event here. Nothing else starts a reconnect loop. That single rule is
 * what the amendment is mostly about: the failure mode it exists to prevent is
 * two independent retry loops racing, each replacing the other's socket and
 * neither owning the outbox.
 *
 * This module is deliberately pure. It takes a clock and a random source, and it
 * returns effects rather than performing them: it never touches a WebSocket,
 * IndexedDB or the DOM. That is what makes the timing rules in §5 testable at
 * all — silent blackholing, delayed pongs, obsolete-generation callbacks and
 * retry budgets are all about *when* things happen, and a test that had to wait
 * 44 real seconds to observe the blackhole bound would not be written.
 */

/** §3: transport lifecycle of the selected candidate. */
export type TransportState =
  | 'DISCONNECTED'
  | 'DIALING'
  | 'AUTHENTICATING'
  | 'OPEN'
  | 'PROBING'
  | 'BACKOFF';

/** §3: progress of the essential recovery barrier (§7). */
export type RecoveryState = 'REQUIRED' | 'RUNNING' | 'READY' | 'DELAYED';

/** §3: a platform hint, never a server health result. */
export type NetworkHint = 'AVAILABLE' | 'UNAVAILABLE' | 'UNKNOWN';

/** §3: states that need a person to do something. */
export type ActionRequired = 'NONE' | 'SIGN_IN' | 'UPDATE_CLIENT' | 'CERTIFICATE_FAILURE';

/** §4: what the status row shows, evaluated top to bottom. */
export type DisplayStatus =
  | 'SIGN_IN'
  | 'UPDATE_CLIENT'
  | 'CERTIFICATE_FAILURE'
  | 'WAITING_FOR_NETWORK'
  | 'CONNECTING'
  | 'UPDATING'
  | 'UPDATING_DELAYED'
  | 'CONNECTED';

// ---------------------------------------------------------------------------
// §5 timer table. Every value here is from the amendment, not invented.
// ---------------------------------------------------------------------------

/** Advertised foreground heartbeat. The server sends this in `heartbeat_ms`. */
export const HEARTBEAT_ADVERTISED_MS = 20_000;
/** After each matched response, the next Ping is due at a uniform 16–24 s. */
export const HEARTBEAT_MIN_MS = 16_000;
export const HEARTBEAT_MAX_MS = 24_000;
/** A Ping unanswered this long means the round trip is unconfirmed: PROBING. */
export const PROBE_SOFT_DEADLINE_MS = 6_000;
/** A Ping unanswered this long invalidates the socket. Also bounds a stuck writer. */
export const PROBE_HARD_DEADLINE_MS = 20_000;
/** Whole connection round: ticket, dial, TLS, Hello and Auth. */
export const CONNECTION_ROUND_MS = 30_000;
/** Recovery with no result for this long is DELAYED; the request stays in flight. */
export const RECOVERY_DELAYED_MS = 10_000;
/** Ordinary visible transitions are debounced this long. */
export const STATUS_DEBOUNCE_MS = 750;
/** Network events coalesce for this long before acting. */
export const NETWORK_DEBOUNCE_MS = 250;
/** Retry cap base and ceiling: min(60000, 500 * 2^min(n,7)). */
export const RETRY_BASE_MS = 500;
export const RETRY_CAP_MS = 60_000;
export const RETRY_SHIFT_MAX = 7;
/** Even with full jitter, two rounds are never closer together than this. */
export const RETRY_MIN_SPACING_MS = 250;
/** Backoff resets only after this long continuously OPEN and READY. */
export const BACKOFF_RESET_MS = 30_000;
/** At most one accelerated round in any rolling window of this length. */
export const IMMEDIATE_RETRY_WINDOW_MS = 5_000;
/** While the hint is negative and the service is unreachable, retry this often. */
export const NEGATIVE_HINT_RETRY_MS = 30_000;

/**
 * §5: the worst case before a silent dead path is detected, with a responsive
 * event loop — at most 24 s until the Ping plus its 20 s hard deadline.
 * Asserted by a test rather than left as prose.
 */
export const BLACKHOLE_DETECTION_BOUND_MS = HEARTBEAT_MAX_MS + PROBE_HARD_DEADLINE_MS;

// ---------------------------------------------------------------------------
// Events in, effects out
// ---------------------------------------------------------------------------

export type ControllerEvent =
  | { type: 'START' }
  | { type: 'STOP' }
  | { type: 'DIAL_STARTED'; generation: number }
  | { type: 'TRANSPORT_OPEN'; generation: number }
  | { type: 'AUTHENTICATED'; generation: number; heartbeatMs?: number }
  /** A response that matched an outstanding request on the selected connection. */
  | { type: 'MATCHED_RESPONSE'; generation: number; rttMs?: number }
  /** A Ping reply. `nonce` must equal the outstanding probe's nonce. */
  | { type: 'PING_REPLY'; generation: number; nonce: bigint; rttMs?: number }
  | { type: 'TRANSPORT_FAILED'; generation: number; retryable: boolean }
  | { type: 'ACTION_REQUIRED'; action: Exclude<ActionRequired, 'NONE'> }
  | { type: 'RECOVERY_STARTED' }
  | { type: 'RECOVERY_PAGE_COMMITTED' }
  | { type: 'RECOVERY_READY' }
  | { type: 'RECOVERY_FAILED' }
  | { type: 'NETWORK_HINT'; hint: NetworkHint }
  | { type: 'RETRY_NOW' }
  | { type: 'FOREGROUNDED' }
  | { type: 'SERVER_RETRY_AFTER'; retryAfterMs: number }
  | { type: 'TICK' };

export type ControllerEffect =
  | { type: 'DIAL'; generation: number }
  | { type: 'SEND_PING'; generation: number; nonce: bigint }
  | { type: 'CLOSE_SOCKET'; generation: number; reason: string }
  | { type: 'START_RECOVERY' }
  | { type: 'STATUS'; status: DisplayStatus };

export interface ControllerDeps {
  /** Monotonic milliseconds. §5 forbids wall-clock for active deadlines. */
  now: () => number;
  /** Uniform [0,1). Injected so jitter is reproducible under test. */
  random: () => number;
}

export interface ControllerSnapshot {
  transport: TransportState;
  recovery: RecoveryState;
  hint: NetworkHint;
  action: ActionRequired;
  generation: number;
  retryAttempt: number;
  /** Debounced status the UI should render. */
  status: DisplayStatus;
  /** Status before debouncing, for assertions and internal decisions. */
  pendingStatus: DisplayStatus;
}

/**
 * The serialized controller.
 *
 * Call `handle` with one event at a time and apply the effects it returns, in
 * order. `TICK` drives every deadline, so a test advances a fake clock and ticks
 * rather than waiting.
 */
export class ConnectionController {
  private readonly deps: ControllerDeps;

  private running = false;
  private transport: TransportState = 'DISCONNECTED';
  private recovery: RecoveryState = 'REQUIRED';
  private hint: NetworkHint = 'UNKNOWN';
  private action: ActionRequired = 'NONE';

  /**
   * §3: in-memory monotonic counter. Every asynchronous callback carries the
   * generation it was issued under, and anything from an older one is ignored
   * for state, cursor and UI purposes. Without it a socket replaced during a
   * network switch can deliver a late success and revive itself.
   */
  private generation = 0;
  private retryAttempt = 0;

  private lastRoundTripAt: number | null = null;
  private heartbeatDueAt: number | null = null;
  private probeSentAt: number | null = null;
  private probeNonce: bigint | null = null;
  private nextAttemptAt: number | null = null;
  private serverRetryNotBefore: number | null = null;
  private roundStartedAt: number | null = null;
  private recoveryStartedAt: number | null = null;
  private steadySince: number | null = null;
  private lastAcceleratedAt: number | null = null;
  private lastNegativeHintRetryAt: number | null = null;

  private status: DisplayStatus = 'CONNECTING';
  private pendingStatus: DisplayStatus = 'CONNECTING';
  private statusChangeAt: number | null = null;

  private nextNonce = 1n;

  constructor(deps: ControllerDeps) {
    this.deps = deps;
  }

  snapshot(): ControllerSnapshot {
    return {
      transport: this.transport,
      recovery: this.recovery,
      hint: this.hint,
      action: this.action,
      generation: this.generation,
      retryAttempt: this.retryAttempt,
      status: this.status,
      pendingStatus: this.pendingStatus,
    };
  }

  handle(event: ControllerEvent): ControllerEffect[] {
    const effects: ControllerEffect[] = [];
    this.apply(event, effects);
    this.settleStatus(effects);
    return effects;
  }

  /**
   * §3: the generation fence.
   *
   * A callback tagged with an older generation belongs to a socket that has
   * already been replaced, and must not change state, cursors or the UI. Without
   * this, a late success from a socket abandoned during a network switch revives
   * it, and two connections believe they own the outbox.
   */
  private current(generation: number): boolean {
    return generation === this.generation;
  }

  private apply(event: ControllerEvent, effects: ControllerEffect[]): void {
    switch (event.type) {
      case 'START':
        this.running = true;
        this.beginRound(effects);
        return;

      case 'STOP':
        this.running = false;
        this.transport = 'DISCONNECTED';
        this.clearProbe();
        return;

      case 'DIAL_STARTED':
        if (!this.current(event.generation)) return;
        this.transport = 'DIALING';
        return;

      case 'TRANSPORT_OPEN':
        if (!this.current(event.generation)) return;
        // §6.1 step 3: a socket-open callback alone cannot select Connected.
        this.transport = 'AUTHENTICATING';
        return;

      case 'AUTHENTICATED': {
        if (!this.current(event.generation)) return;
        this.transport = 'OPEN';
        this.roundStartedAt = null;
        this.lastRoundTripAt = this.deps.now();
        this.scheduleHeartbeat();
        // §7: authentication does not mean ready. The barrier has to run.
        if (this.recovery !== 'READY') {
          this.recovery = 'REQUIRED';
          effects.push({ type: 'START_RECOVERY' });
        }
        return;
      }

      case 'MATCHED_RESPONSE':
        if (!this.current(event.generation)) return;
        // §5: a valid matched response is liveness evidence even while a Ping is
        // outstanding. Retire the probe and schedule the next interval.
        this.confirmRoundTrip();
        return;

      case 'PING_REPLY':
        if (!this.current(event.generation)) return;
        // §5: Ping requires an exact nonce match. A reply carrying an old nonce
        // is not evidence that the current probe was answered — it is exactly
        // what a delayed reply from a stalled path looks like.
        if (this.probeNonce === null || event.nonce !== this.probeNonce) return;
        this.confirmRoundTrip();
        return;

      case 'TRANSPORT_FAILED':
        if (!this.current(event.generation)) return;
        this.failRound(effects, event.retryable);
        return;

      case 'ACTION_REQUIRED':
        // §4: these bypass the debounce and stop automatic retrying.
        this.action = event.action;
        this.transport = 'DISCONNECTED';
        this.clearProbe();
        this.nextAttemptAt = null;
        return;

      case 'RECOVERY_STARTED':
        this.recovery = 'RUNNING';
        this.recoveryStartedAt = this.deps.now();
        return;

      case 'RECOVERY_PAGE_COMMITTED':
        // §4: a completed page returns DELAYED to RUNNING.
        if (this.recovery === 'DELAYED' || this.recovery === 'RUNNING') {
          this.recovery = 'RUNNING';
          this.recoveryStartedAt = this.deps.now();
        }
        return;

      case 'RECOVERY_READY':
        this.recovery = 'READY';
        this.recoveryStartedAt = null;
        this.markSteady();
        return;

      case 'RECOVERY_FAILED':
        // §5: an RPC failure is not proof the socket is broken. The connection
        // is retained and recovery retries on its own schedule.
        this.recovery = 'DELAYED';
        this.steadySince = null;
        return;

      case 'NETWORK_HINT':
        this.applyNetworkHint(event.hint, effects);
        return;

      case 'RETRY_NOW':
      case 'FOREGROUNDED':
        this.accelerate(effects);
        return;

      case 'SERVER_RETRY_AFTER':
        // §5: a server-directed delay is respected even by Retry now.
        this.serverRetryNotBefore = this.deps.now() + event.retryAfterMs;
        if (this.nextAttemptAt === null || this.nextAttemptAt < this.serverRetryNotBefore) {
          this.nextAttemptAt = this.serverRetryNotBefore;
        }
        return;

      case 'TICK':
        this.tick(effects);
        return;
    }
  }

  // -------------------------------------------------------------------------
  // Rounds and retries
  // -------------------------------------------------------------------------

  /** §3: a new candidate invalidates every callback from the previous one. */
  private beginRound(effects: ControllerEffect[]): void {
    if (!this.running || this.action !== 'NONE') return;
    this.generation += 1;
    this.transport = 'DIALING';
    this.roundStartedAt = this.deps.now();
    this.clearProbe();
    effects.push({ type: 'DIAL', generation: this.generation });
  }

  private failRound(effects: ControllerEffect[], retryable: boolean): void {
    this.clearProbe();
    this.steadySince = null;
    this.transport = 'BACKOFF';
    if (!retryable || !this.running) {
      this.transport = 'DISCONNECTED';
      return;
    }
    this.scheduleRetry();
  }

  /**
   * §5 retry cap: `cap = min(60000, 500 * 2^min(n,7))`, delay uniform in
   * `[0, cap]`, with an actual minimum spacing of 250 ms.
   *
   * Full jitter rather than a fixed backoff because the failure this guards
   * against is a fleet reconnecting in lockstep after a shared outage.
   */
  private scheduleRetry(): void {
    const shift = Math.min(this.retryAttempt, RETRY_SHIFT_MAX);
    const cap = Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** shift);
    const delay = Math.max(RETRY_MIN_SPACING_MS, Math.floor(this.deps.random() * cap));
    this.retryAttempt += 1;

    let at = this.deps.now() + delay;
    if (this.serverRetryNotBefore !== null && at < this.serverRetryNotBefore) {
      at = this.serverRetryNotBefore;
    }
    this.nextAttemptAt = at;
  }

  /**
   * §5: at most one accelerated round in any rolling 5 seconds, across network
   * changes, Retry now and foregrounding together — they share one budget so
   * three sources cannot each grant themselves an attempt.
   */
  private accelerate(effects: ControllerEffect[]): void {
    if (!this.running || this.action !== 'NONE') return;

    const now = this.deps.now();
    if (this.transport === 'OPEN' || this.transport === 'AUTHENTICATING') {
      // Already connected: probe rather than replace a working socket.
      if (this.transport === 'OPEN') this.startProbe(effects);
      return;
    }
    if (this.serverRetryNotBefore !== null && now < this.serverRetryNotBefore) return;
    if (
      this.lastAcceleratedAt !== null &&
      now - this.lastAcceleratedAt < IMMEDIATE_RETRY_WINDOW_MS
    ) {
      return;
    }

    this.lastAcceleratedAt = now;
    this.nextAttemptAt = null;
    this.beginRound(effects);
  }

  private applyNetworkHint(hint: NetworkHint, effects: ControllerEffect[]): void {
    this.hint = hint;
    if (hint !== 'UNAVAILABLE') {
      // §5: restoration gets a budgeted attempt rather than an immediate storm.
      this.accelerate(effects);
      return;
    }
    // §4: a negative hint while a working connection has fresh evidence must not
    // be believed on its own. Probe, and keep the current state until the probe
    // soft deadline; a successful response overrides the hint.
    if (this.transport === 'OPEN') {
      this.startProbe(effects);
    }
  }

  // -------------------------------------------------------------------------
  // Heartbeat and probing
  // -------------------------------------------------------------------------

  private scheduleHeartbeat(): void {
    const span = HEARTBEAT_MAX_MS - HEARTBEAT_MIN_MS;
    this.heartbeatDueAt = this.deps.now() + HEARTBEAT_MIN_MS + Math.floor(this.deps.random() * span);
  }

  private startProbe(effects: ControllerEffect[]): void {
    if (this.probeSentAt !== null) return;
    this.probeSentAt = this.deps.now();
    this.probeNonce = this.nextNonce;
    this.nextNonce += 1n;
    this.heartbeatDueAt = null;
    effects.push({ type: 'SEND_PING', generation: this.generation, nonce: this.probeNonce });
  }

  private confirmRoundTrip(): void {
    this.lastRoundTripAt = this.deps.now();
    this.clearProbe();
    if (this.transport === 'PROBING') this.transport = 'OPEN';
    this.scheduleHeartbeat();
    this.markSteady();
  }

  private clearProbe(): void {
    this.probeSentAt = null;
    this.probeNonce = null;
  }

  private markSteady(): void {
    if (this.transport === 'OPEN' && this.recovery === 'READY' && this.steadySince === null) {
      this.steadySince = this.deps.now();
    }
  }

  /** Every deadline in §5 is evaluated here, so one clock drives all of them. */
  private tick(effects: ControllerEffect[]): void {
    const now = this.deps.now();

    // A due retry.
    if (
      this.nextAttemptAt !== null &&
      now >= this.nextAttemptAt &&
      this.transport !== 'OPEN' &&
      this.transport !== 'AUTHENTICATING' &&
      this.transport !== 'DIALING'
    ) {
      this.nextAttemptAt = null;
      this.beginRound(effects);
      return;
    }

    // §5: the whole round, including ticket, dial, TLS, Hello and Auth.
    if (
      this.roundStartedAt !== null &&
      now - this.roundStartedAt >= CONNECTION_ROUND_MS &&
      (this.transport === 'DIALING' || this.transport === 'AUTHENTICATING')
    ) {
      effects.push({
        type: 'CLOSE_SOCKET',
        generation: this.generation,
        reason: 'connection round deadline',
      });
      this.roundStartedAt = null;
      this.failRound(effects, true);
      return;
    }

    // A due heartbeat.
    if (this.transport === 'OPEN' && this.heartbeatDueAt !== null && now >= this.heartbeatDueAt) {
      this.startProbe(effects);
    }

    if (this.probeSentAt !== null) {
      const elapsed = now - this.probeSentAt;
      // §5 hard deadline: the socket is not trustworthy; replace it. Checked
      // before the soft deadline so one tick that crosses both does the
      // stronger thing.
      if (elapsed >= PROBE_HARD_DEADLINE_MS) {
        effects.push({
          type: 'CLOSE_SOCKET',
          generation: this.generation,
          reason: 'probe hard deadline',
        });
        this.failRound(effects, true);
        return;
      }
      // §5 soft deadline: the round trip is unconfirmed. §4: project Connecting
      // even if the operating system still reports an open socket.
      if (elapsed >= PROBE_SOFT_DEADLINE_MS && this.transport === 'OPEN') {
        this.transport = 'PROBING';
        this.steadySince = null;
      }
    }

    // §5: recovery with no result for 10 s is DELAYED but stays in flight.
    if (
      this.recovery === 'RUNNING' &&
      this.recoveryStartedAt !== null &&
      now - this.recoveryStartedAt >= RECOVERY_DELAYED_MS
    ) {
      this.recovery = 'DELAYED';
      this.steadySince = null;
    }

    // §5: backoff resets only after 30 continuous seconds OPEN and READY.
    if (this.steadySince !== null && now - this.steadySince >= BACKOFF_RESET_MS) {
      this.retryAttempt = 0;
    }

    // §5: while a negative hint stands and the service is unreachable, retry at
    // most once every 30 seconds rather than spinning.
    if (
      this.running &&
      this.action === 'NONE' &&
      this.hint === 'UNAVAILABLE' &&
      this.transport !== 'OPEN' &&
      this.transport !== 'DIALING' &&
      this.transport !== 'AUTHENTICATING' &&
      (this.lastNegativeHintRetryAt === null ||
        now - this.lastNegativeHintRetryAt >= NEGATIVE_HINT_RETRY_MS)
    ) {
      this.lastNegativeHintRetryAt = now;
      this.beginRound(effects);
    }
  }

  // -------------------------------------------------------------------------
  // §4 status projection
  // -------------------------------------------------------------------------

  /** §4: the table, evaluated top to bottom. */
  private computeStatus(): DisplayStatus {
    switch (this.action) {
      case 'SIGN_IN':
        return 'SIGN_IN';
      case 'UPDATE_CLIENT':
        return 'UPDATE_CLIENT';
      case 'CERTIFICATE_FAILURE':
        return 'CERTIFICATE_FAILURE';
      default:
        break;
    }

    const responsive = this.transport === 'OPEN';
    if (!responsive && this.hint === 'UNAVAILABLE') return 'WAITING_FOR_NETWORK';
    if (!responsive || this.transport === 'PROBING') return 'CONNECTING';
    if (this.recovery === 'REQUIRED' || this.recovery === 'RUNNING') return 'UPDATING';
    if (this.recovery === 'DELAYED') return 'UPDATING_DELAYED';
    return 'CONNECTED';
  }

  /**
   * §4: apply state changes internally immediately, debounce the visible
   * transition by 750 ms, and let a superseding transition replace a pending
   * one. Authentication and certificate failures bypass the debounce, because
   * they need the person to act and a delay there is just a slower answer.
   */
  private settleStatus(effects: ControllerEffect[]): void {
    const next = this.computeStatus();
    const now = this.deps.now();

    if (next === this.status) {
      this.pendingStatus = next;
      this.statusChangeAt = null;
      return;
    }

    const immediate =
      next === 'SIGN_IN' || next === 'UPDATE_CLIENT' || next === 'CERTIFICATE_FAILURE';

    if (this.pendingStatus !== next) {
      this.pendingStatus = next;
      this.statusChangeAt = now;
    }

    if (immediate || (this.statusChangeAt !== null && now - this.statusChangeAt >= STATUS_DEBOUNCE_MS)) {
      this.status = next;
      this.statusChangeAt = null;
      effects.push({ type: 'STATUS', status: next });
    }
  }
}
